import { Link } from "react-router-dom";
import { Button } from "./ui/button";
import { ArrowLeft, ArrowRight, Check, MapPin, Phone, Building2 } from "lucide-react";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import {
  ABOUT_HERO, ABOUT_STATS, ABOUT_STORY, ABOUT_SUPPORTED,
  ABOUT_INDUSTRIES, ABOUT_ECOSYSTEM, ABOUT_CONTINUITY,
  ABOUT_VISION, ABOUT_VALUES, ABOUT_WHY_STAY, ABOUT_FUTURE,
  ABOUT_CREDENTIALS, ABOUT_LOCATIONS
} from "../data/aboutData";

const Section = ({ id, bg = "bg-white", children }) => (
  <section id={id} className={`py-20 lg:py-28 ${bg}`}>
    <div className="max-w-7xl mx-auto px-6">{children}</div>
  </section>
);

export default function AboutPage() {
  return (
    <>
      {/* Hero */}
      <section className="relative min-h-[75vh] flex flex-col justify-center bg-[#2C2A2B] overflow-hidden">
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-[#308795]/[0.06] rounded-full -translate-y-1/3 translate-x-1/3" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-[#E5BD77]/[0.04] rounded-full translate-y-1/3 -translate-x-1/4" />
        <div className="relative max-w-7xl mx-auto px-6 pt-32 pb-16 lg:pt-40 lg:pb-24 w-full">
          <div className="flex items-center justify-between mb-8">
            <Link to="/" className="inline-flex items-center gap-2 text-sm text-white/40 hover:text-[#E5BD77] transition-colors">
              <ArrowLeft className="w-4 h-4" /> Back to Home
            </Link>
            <div className="inline-flex items-center gap-2 bg-[#E5BD77]/15 text-[#E5BD77] text-sm font-semibold px-4 py-1.5 rounded-full">
              <span className="w-2 h-2 bg-[#E5BD77] rounded-full animate-pulse" />
              {ABOUT_HERO.eyebrow}
            </div>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.1] tracking-tight mb-2 max-w-4xl">
            {ABOUT_HERO.headline}
          </h1>
          <p className="text-2xl lg:text-3xl font-bold text-[#E5BD77] mb-5">{ABOUT_HERO.since}</p>
          <p className="text-lg lg:text-xl text-[#A9B294] leading-relaxed max-w-2xl mb-12">{ABOUT_HERO.subheadline}</p>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {ABOUT_STATS.map((s, i) => {
              const Icon = s.icon;
              return (
                <div key={i} className="bg-white/[0.05] border border-white/[0.08] rounded-xl px-5 py-4 flex items-center gap-4">
                  <div className="w-10 h-10 rounded-lg bg-[#E5BD77]/15 flex items-center justify-center shrink-0">
                    <Icon className="w-5 h-5 text-[#E5BD77]" />
                  </div>
                  <div>
                    <div className="text-lg font-bold text-white">{s.value}</div>
                    <div className="text-xs text-white/50 font-medium">{s.label}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Story */}
      <Section bg="bg-[#FAFAF7]">
        <div className="lg:grid lg:grid-cols-2 lg:gap-16 items-start">
          <div>
            <span className="text-sm font-bold text-[#308795] uppercase tracking-widest mb-3 block">Our Story</span>
            <h2 className="text-3xl lg:text-4xl font-bold text-[#2C2A2B] leading-tight mb-6">{ABOUT_STORY.headline}</h2>
            {ABOUT_STORY.paragraphs.map((p, i) => (
              <p key={i} className="text-[#6B6567] text-base leading-relaxed mb-4">{p}</p>
            ))}
          </div>
          <div className="mt-10 lg:mt-0">
            <div className="bg-white border border-[#E0D1BB]/30 rounded-2xl p-8">
              <h3 className="text-sm font-bold text-[#E95D54] uppercase tracking-widest mb-5">Technology Waves We've Navigated</h3>
              <div className="space-y-3">
                {ABOUT_STORY.waves.map((w, i) => (
                  <div key={i} className="flex items-center gap-4">
                    <span className="text-xs font-bold text-[#308795] bg-[#308795]/[0.08] w-7 h-7 rounded-full flex items-center justify-center shrink-0">{i + 1}</span>
                    <span className="text-sm font-medium text-[#494546]">{w}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-white border border-[#E0D1BB]/30 rounded-2xl p-8 mt-5">
              <h3 className="text-sm font-bold text-[#7EA13E] uppercase tracking-widest mb-4">We've Supported Organisations Through</h3>
              <div className="grid grid-cols-2 gap-2">
                {ABOUT_SUPPORTED.map((s, i) => (
                  <div key={i} className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-[#7EA13E] mt-0.5 shrink-0" />
                    <span className="text-xs text-[#494546]">{s}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* Vision & Mission */}
      <Section bg="bg-[#465155]">
        <div className="lg:grid lg:grid-cols-2 lg:gap-12">
          <div className="bg-white/[0.06] border border-white/[0.08] rounded-2xl p-8 mb-6 lg:mb-0">
            <span className="text-sm font-bold text-[#E5BD77] uppercase tracking-widest mb-3 block">Our Vision</span>
            <p className="text-lg text-white leading-relaxed font-medium">{ABOUT_VISION.vision}</p>
          </div>
          <div className="bg-white/[0.06] border border-white/[0.08] rounded-2xl p-8">
            <span className="text-sm font-bold text-[#7EA13E] uppercase tracking-widest mb-3 block">Our Mission</span>
            <p className="text-lg text-white leading-relaxed font-medium">{ABOUT_VISION.mission}</p>
          </div>
        </div>
      </Section>

      {/* Values */}
      <Section bg="bg-white">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <span className="text-sm font-bold text-[#308795] uppercase tracking-widest mb-3 block">What Defines Us</span>
          <h2 className="text-3xl lg:text-4xl font-bold text-[#2C2A2B] leading-tight">Our Core Values</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {ABOUT_VALUES.map((v, i) => {
            const Icon = v.icon;
            return (
              <div key={i} className="bg-[#FAFAF7] border border-[#E0D1BB]/30 rounded-2xl p-6 transition-all duration-300 hover:shadow-md hover:-translate-y-0.5">
                <div className="w-12 h-12 rounded-xl bg-[#308795]/[0.08] flex items-center justify-center mb-4">
                  <Icon className="w-6 h-6 text-[#308795]" />
                </div>
                <h3 className="text-base font-bold text-[#2C2A2B] mb-2">{v.title}</h3>
                <p className="text-sm text-[#6B6567] leading-relaxed">{v.description}</p>
              </div>
            );
          })}
        </div>
      </Section>

      {/* Industries */}
      <Section bg="bg-[#FAFAF7]">
        <div className="max-w-3xl mb-14">
          <span className="text-sm font-bold text-[#E95D54] uppercase tracking-widest mb-3 block">Multi-Industry Experience</span>
          <h2 className="text-3xl lg:text-4xl font-bold text-[#2C2A2B] leading-tight mb-3">Deep Domain Strength</h2>
          <p className="text-[#6B6567] text-lg">From nursing homes to marine cold storage, from grant-driven non-profits to growth-driven distributors — we understand SMEs because we've grown alongside them.</p>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
          {ABOUT_INDUSTRIES.map((ind, i) => {
            const Icon = ind.icon;
            return (
              <div key={i} className="bg-white border border-[#E0D1BB]/30 rounded-xl p-5 text-center transition-all duration-300 hover:shadow-md">
                <div className="w-12 h-12 rounded-xl bg-[#308795]/[0.08] flex items-center justify-center mx-auto mb-3">
                  <Icon className="w-6 h-6 text-[#308795]" />
                </div>
                <p className="text-xs font-semibold text-[#2C2A2B]">{ind.name}</p>
              </div>
            );
          })}
        </div>
      </Section>

      {/* Digital Ecosystem */}
      <Section bg="bg-white">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <span className="text-sm font-bold text-[#7EA13E] uppercase tracking-widest mb-3 block">From Systems Integrator to Innovation Partner</span>
          <h2 className="text-3xl lg:text-4xl font-bold text-[#2C2A2B] leading-tight mb-3">Integrated Digital Ecosystems</h2>
          <p className="text-[#6B6567] text-lg">Asterisk today is more than an ERP implementer. We architect integrated digital ecosystems.</p>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
          {ABOUT_ECOSYSTEM.map((e, i) => {
            const Icon = e.icon;
            return (
              <div key={i} className="bg-[#FAFAF7] border border-[#E0D1BB]/30 rounded-2xl p-5 text-center transition-all duration-300 hover:shadow-md hover:-translate-y-0.5">
                <div className="w-12 h-12 rounded-xl bg-[#E5BD77]/12 flex items-center justify-center mx-auto mb-3">
                  <Icon className="w-6 h-6 text-[#A56A3E]" />
                </div>
                <p className="text-xs font-semibold text-[#2C2A2B] leading-snug">{e.title}</p>
              </div>
            );
          })}
        </div>
      </Section>

      {/* Continuity */}
      <Section bg="bg-[#FAFAF7]">
        <div className="lg:grid lg:grid-cols-2 lg:gap-16 items-center">
          <div>
            <span className="text-sm font-bold text-[#308795] uppercase tracking-widest mb-3 block">Lifecycle Focus</span>
            <h2 className="text-3xl lg:text-4xl font-bold text-[#2C2A2B] leading-tight mb-4">{ABOUT_CONTINUITY.headline}</h2>
            <p className="text-[#6B6567] text-lg leading-relaxed mb-6">{ABOUT_CONTINUITY.description}</p>
            <p className="text-sm font-semibold text-[#308795] italic">"Digital maturity is built over time."</p>
          </div>
          <div className="mt-10 lg:mt-0 space-y-3">
            {ABOUT_CONTINUITY.elements.map((e, i) => (
              <div key={i} className="flex items-center gap-4 bg-white border border-[#E0D1BB]/30 rounded-xl px-5 py-4">
                <span className="text-xs font-bold text-white bg-[#308795] w-7 h-7 rounded-full flex items-center justify-center shrink-0">{i + 1}</span>
                <span className="text-sm font-medium text-[#494546]">{e}</span>
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* Where Innovation Lives */}
      <Section bg="bg-white">
        <div className="max-w-3xl mb-14">
          <span className="text-sm font-bold text-[#E95D54] uppercase tracking-widest mb-3 block">Our Presence</span>
          <h2 className="text-3xl lg:text-4xl font-bold text-[#2C2A2B] leading-tight mb-4">{ABOUT_LOCATIONS.headline}</h2>
          <p className="text-[#6B6567] text-lg leading-relaxed">{ABOUT_LOCATIONS.description}</p>
        </div>

        {/* Interactive Map */}
        <div className="mb-14">
          <div className="relative max-w-4xl mx-auto rounded-2xl border border-[#E0D1BB]/30 overflow-hidden" style={{ height: "420px" }}>
            <MapContainer
              center={[6.5, 105.5]}
              zoom={5}
              scrollWheelZoom={false}
              style={{ height: "100%", width: "100%" }}
              attributionControl={false}
            >
              <TileLayer url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png" />
              {ABOUT_LOCATIONS.offices.map((office, i) => {
                const colors = ["#E95D54", "#308795", "#7EA13E"];
                const markerIcon = L.divIcon({
                  className: "custom-marker",
                  html: `<div style="width:32px;height:32px;border-radius:50%;background:${colors[i]};border:3px solid white;box-shadow:0 2px 8px rgba(0,0,0,0.25);display:flex;align-items:center;justify-content:center"><div style="width:8px;height:8px;border-radius:50%;background:white"></div></div>`,
                  iconSize: [32, 32],
                  iconAnchor: [16, 16],
                });
                return (
                  <Marker key={i} position={[office.lat, office.lng]} icon={markerIcon}>
                    <Popup>
                      <div style={{ fontFamily: "Plus Jakarta Sans, sans-serif", minWidth: 180 }}>
                        <p style={{ fontWeight: 700, fontSize: 14, margin: "0 0 2px", color: "#2C2A2B" }}>{office.city}</p>
                        <p style={{ fontSize: 11, color: colors[i], fontWeight: 600, margin: "0 0 4px" }}>{office.role}</p>
                        <p style={{ fontSize: 11, color: "#6B6567", margin: 0 }}>{office.company}</p>
                      </div>
                    </Popup>
                  </Marker>
                );
              })}
            </MapContainer>
          </div>
        </div>

        {/* Office Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-12">
          {ABOUT_LOCATIONS.offices.map((office, i) => {
            const colors = ["#E95D54", "#308795", "#7EA13E"];
            const color = colors[i] || "#308795";
            return (
            <div key={i} className="bg-[#FAFAF7] border border-[#E0D1BB]/30 rounded-2xl p-7 transition-all duration-300 hover:shadow-md">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: color + "18" }}>
                  <Building2 className="w-5 h-5" style={{ color }} />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-[#2C2A2B]">{office.city}</h3>
                  <p className="text-xs font-semibold" style={{ color }}>{office.role}</p>
                </div>
              </div>
              <p className="text-sm font-semibold text-[#494546] mb-1">{office.company}</p>
              <div className="flex items-start gap-2 mb-1">
                <MapPin className="w-3.5 h-3.5 text-[#8A8385] mt-0.5 shrink-0" />
                <p className="text-xs text-[#6B6567]">{office.address}</p>
              </div>
              <div className="flex items-center gap-2 mb-4">
                <Phone className="w-3.5 h-3.5 text-[#8A8385] shrink-0" />
                <p className="text-xs text-[#6B6567]">{office.phone}</p>
              </div>
              <p className="text-xs text-[#6B6567] italic mb-4">{office.detail}</p>
              <div className="space-y-1.5">
                {office.services.map((s, j) => (
                  <div key={j} className="flex items-start gap-2">
                    <Check className="w-3.5 h-3.5 mt-0.5 shrink-0" style={{ color }} />
                    <span className="text-xs text-[#494546]">{s}</span>
                  </div>
                ))}
              </div>
            </div>
            );
          })}
        </div>

        {/* Closing */}
        <div className="bg-[#465155] rounded-2xl p-8 lg:p-10">
          <h3 className="text-xl font-bold text-white mb-4">{ABOUT_LOCATIONS.closingHeadline}</h3>
          <div className="flex flex-wrap gap-3 mb-5">
            {ABOUT_LOCATIONS.closingPoints.map((p, i) => (
              <span key={i} className="text-xs font-medium bg-white/[0.08] border border-white/[0.1] text-[#E5BD77] px-4 py-2 rounded-lg">{p}</span>
            ))}
          </div>
          <p className="text-sm text-[#D5CFC9] leading-relaxed">{ABOUT_LOCATIONS.closingText}</p>
        </div>
      </Section>

      {/* Why Clients Stay + Credentials */}
      <Section bg="bg-[#465155]">
        <div className="lg:grid lg:grid-cols-2 lg:gap-16">
          <div>
            <span className="text-sm font-bold text-[#E5BD77] uppercase tracking-widest mb-3 block">Client Retention</span>
            <h2 className="text-3xl font-bold text-white leading-tight mb-6">Why Clients Choose to Stay</h2>
            <div className="space-y-3">
              {ABOUT_WHY_STAY.map((r, i) => (
                <div key={i} className="flex items-start gap-3 bg-white/[0.06] border border-white/[0.08] rounded-xl px-5 py-3">
                  <Check className="w-4 h-4 text-[#E5BD77] mt-0.5 shrink-0" />
                  <span className="text-sm text-[#D5CFC9]">{r}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="mt-10 lg:mt-0">
            <span className="text-sm font-bold text-[#7EA13E] uppercase tracking-widest mb-3 block">Credentials</span>
            <h2 className="text-3xl font-bold text-white leading-tight mb-6">Microsoft Partnership</h2>
            <div className="space-y-3">
              {ABOUT_CREDENTIALS.map((c, i) => (
                <div key={i} className="flex items-start gap-3 bg-white/[0.06] border border-white/[0.08] rounded-xl px-5 py-3">
                  <Check className="w-4 h-4 text-[#7EA13E] mt-0.5 shrink-0" />
                  <span className="text-sm text-[#D5CFC9]">{c}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </Section>

      {/* Looking Forward */}
      <Section bg="bg-white">
        <div className="max-w-3xl mx-auto text-center">
          <span className="text-sm font-bold text-[#E95D54] uppercase tracking-widest mb-3 block">Looking Forward</span>
          <h2 className="text-3xl lg:text-4xl font-bold text-[#2C2A2B] leading-tight mb-4">The Next Decade Will Redefine SMEs</h2>
          <p className="text-[#6B6567] text-lg leading-relaxed mb-10">Asterisk is positioned not only to support this shift — but to guide it.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-10">
            {ABOUT_FUTURE.map((f, i) => (
              <div key={i} className="bg-[#FAFAF7] border border-[#E0D1BB]/30 rounded-xl px-5 py-4 text-center">
                <p className="text-sm font-semibold text-[#2C2A2B]">{f}</p>
              </div>
            ))}
          </div>
          <Link to="/consultation">
            <Button className="bg-[#308795] hover:bg-[#267580] text-white font-semibold px-8 py-6 text-base rounded-xl transition-all duration-300 hover:shadow-xl hover:shadow-[#308795]/20 hover:-translate-y-0.5 group">
              Book a Strategy Consultation
              <ArrowRight className="ml-2 h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Button>
          </Link>
        </div>
      </Section>
    </>
  );
}
